import {
  Audience,
  AudienceType,
  CampaignBEData,
  CampaignFEValues,
  CampaignTriggers,
  CampaignVisibility,
  DirectCampaignRule,
  ReferralCampaignRule,
  TransactionCampaigns,
  VisibilityTarget,
} from "types/campaigns";
import { array, object, string } from "yup";
import { isNewFormat } from "../../Common/campaignHelpers";

const campaignSchema = object({
  activity: object({}).required(),
  translations: array()
    .of(
      object({
        name: string(),
        locale: string(),
      }),
    )
    .required(),
  rules: array().required(),
  trigger: string()
    .oneOf([
      CampaignTriggers.TRANSACTION,
      CampaignTriggers.INTERNAL_EVENT,
      CampaignTriggers.CUSTOM_EVENT,
      CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE,
      CampaignTriggers.ACHIEVEMENT,
      CampaignTriggers.TIME,
      CampaignTriggers.RETURN_TRANSACTION,
    ])
    .required(),
});

export const isCampaignJsonValid = (campaign: any) => {
  try {
    campaignSchema.validateSync(campaign);

    return true;
  } catch {
    return false;
  }
};

const filterImportedRules = (
  rules: (DirectCampaignRule | ReferralCampaignRule)[],
) => {
  const attributesRequiredActions = [
    "customer.levelId",
    "customer.tiers",
    "customer.segments",
    "referrer.segments",
    "referrer.levelId",
    "referrer.tiers",
  ];

  const operatorsRequiredActions = [
    "is_one_of_group_values",
    "is_not_one_of_group_values",
  ];

  return rules.map(({ conditions, effects, name, description, ...rule }) => {
    return {
      ...(name ? { name } : {}),
      ...(description ? { description } : {}),
      effects: effects?.map(({ ...rest }) => ({
        ...rest,
      })),
      conditions: conditions?.map((condition) =>
        isNewFormat(condition)
          ? { ...condition }
          : {
              ...condition,
              data:
                attributesRequiredActions.includes(condition.attribute) ||
                operatorsRequiredActions.includes(condition.operator)
                  ? null
                  : condition.data,
            },
      ),
      ...("target" in rule ? { target: rule.target } : {}),
    };
  });
};

const formatVisibility = (
  visibility: CampaignVisibility,
): CampaignVisibility => {
  switch (visibility.target) {
    case VisibilityTarget.TIER:
      return {
        target: VisibilityTarget.TIER,
        tiers: [],
      };
    case VisibilityTarget.SEGMENT:
      return {
        target: VisibilityTarget.SEGMENT,
        segments: [],
      };
    default:
      return visibility;
  }
};

const formatAudience = (audience?: Audience) => {
  if (audience) {
    switch (audience.target) {
      case AudienceType.TIER:
        return {
          target: AudienceType.TIER,
          tiers: [],
        };
      case AudienceType.SEGMENT:
        return {
          target: AudienceType.SEGMENT,
          segments: [],
        };
      default:
        return audience;
    }
  } else {
    return;
  }
};

export const formatImportedCampaigns = (
  campaigns: (CampaignBEData | CampaignFEValues)[],
) =>
  campaigns.map(
    ({
      activity,
      displayOrder,
      limits,
      name,
      translations,
      trigger,
      type,
      rules,
      visibility,
      limitUsages,
      labels,
      campaignId,
      ...data
    }) => {
      const transactionItemsFilters =
        "transactionItemsFilters" in data
          ? (data as TransactionCampaigns).transactionItemsFilters
          : undefined;

      return {
        type,
        name,
        trigger,
        limits,
        labels,
        activity,
        campaignId,
        limitUsages,
        displayOrder,
        translations,
        active: false,
        transactionItemsFilters,
        description: "description" in data ? data.description : undefined,
        triggerStrategy:
          "triggerStrategy" in data ? data.triggerStrategy : undefined,
        multiLevel: "multiLevel" in data ? data.multiLevel : undefined,
        event: "event" in data ? data.event : undefined,
        codeGenerator: "codeGenerator" in data ? data.codeGenerator : undefined,
        eventCodeAttribute:
          "eventCodeAttribute" in data ? data.eventCodeAttribute : undefined,
        generateCodes: "generateCodes" in data ? data.generateCodes : undefined,
        rules: filterImportedRules(rules),
        ...(visibility ? { visibility: formatVisibility(visibility) } : {}),
        ...("audience" in data
          ? { audience: formatAudience(data.audience) }
          : {}),
      };
    },
  );
